import axios, { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import { ACCESS_TOKEN_KEY } from './constants';
import * as querystring from 'query-string';
import { OwlClientInput } from '../../types/owl';

type RouteArg<T> = string | ((args: T) => string);

function getFormDataFromObject(data: Record<string, any>) {
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      formData.append(key, typeof value === 'boolean' ? +value : value);
    }
  }
  return formData;
}

export abstract class WebClientStorage {
  abstract getItem(key: string): string | null | Promise<string | null>;

  abstract setItem(key: string, value: string): void | Promise<void>;

  abstract removeItem(key: string): void | Promise<void>;
}

export class BrowserWebClientStorage extends WebClientStorage {
  getItem(key: string) {
    return window.localStorage.getItem(key);
  }

  setItem(key: string, value: string) {
    window.localStorage.setItem(key, value);
  }

  removeItem(key: string) {
    window.localStorage.removeItem(key);
  }
}

export class LocalWebClientStorage extends WebClientStorage {
  private data: Record<string, string> = {};

  getItem(key: string) {
    return this.data[key];
  }

  setItem(key: string, value: string) {
    this.data[key] = value;
  }

  removeItem(key: string) {
    delete this.data[key];
  }
}

export abstract class AbstractClient {
  protected client: AxiosInstance;

  constructor(
    baseURL: string,
    protected storage: WebClientStorage,
    hasAuth?: boolean,
  ) {
    this.client = axios.create({ baseURL });
    if (hasAuth) {
      this.client.interceptors.request.use(this.authInterceptor);
    }
  }

  public async setAuthToken(token: string) {
    await this.storage.setItem(ACCESS_TOKEN_KEY, token);
  }

  public async clearAuthToken() {
    await this.storage.removeItem(ACCESS_TOKEN_KEY);
  }

  readonly authInterceptor = async (req: AxiosRequestConfig) => {
    const token = await this.storage.getItem(ACCESS_TOKEN_KEY);
    if (token && req.headers && !req.headers['Authorization']) {
      req.headers['Authorization'] = `Bearer ${token}`;
    }
    return req;
  };

  protected createGetHandler<T, U extends Omit<OwlClientInput, 'payload'>>(
    route: RouteArg<U['args']>,
    baseQuery?: Record<string, any>,
  ) {
    return async (input: U): Promise<T> => {
      const response = await this.client.get<T>(
        typeof route === 'string' ? route : route(input.args),
        {
          paramsSerializer: (params) =>
            querystring.stringify(params, {
              skipEmptyString: true,
              skipNull: true,
              strict: true,
              arrayFormat: 'bracket',
            }),
          params: { ...baseQuery, ...input.query },
        },
      );
      return response.data;
    };
  }

  protected createPayloadHandler<T, U extends OwlClientInput>(
    method: Method,
    route: RouteArg<U['args']>,
    options?: {
      isFormData?: boolean;
    },
    baseQuery?: Record<string, any>,
  ) {
    return async (input: U): Promise<T> => {
      const response = await this.client.request<T>({
        method,
        data: options?.isFormData
          ? getFormDataFromObject(input.payload)
          : input.payload,
        url: typeof route === 'string' ? route : route(input.args),
        params: { ...baseQuery, ...input.query },
      });
      return response.data;
    };
  }

  async setAccessToken(token: string) {
    this.storage.setItem(ACCESS_TOKEN_KEY, token);
  }
}
